import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { boundedNfcTextSchema, normalizedSnowflakeListSchema, opaqueCursorSchema, } from "./platform-common.js";
export const MAX_SELECTOR_PAGE_SIZE = 50;
export const SELECTOR_SNAPSHOT_FRESHNESS_MILLISECONDS = 2 * 60 * 60 * 1_000;
export const MAX_ASSISTANT_ROLE_IDS = 50;
export const MAX_ASSISTANT_MEMBER_IDS = 100;
export const MAX_SYNCHRONIZED_ROLES = 1_000;
export const MAX_SYNCHRONIZED_MEMBERS_PER_BATCH = 1_000;
export const MAX_SYNCHRONIZED_MEMBER_ROLE_EDGES_PER_BATCH = 50_000;
const validDateSchema = z
    .date()
    .refine((value) => Number.isFinite(value.getTime()), "Invalid date");
const selectorSearchSchema = boundedNfcTextSchema(100, 512);
export const synchronizedGuildRoleSchema = z
    .object({
    roleId: discordSnowflakeSchema,
    name: boundedNfcTextSchema(100, 512),
    position: z.int().nonnegative(),
    managed: z.boolean(),
})
    .strict();
export const synchronizedGuildMemberSchema = z
    .object({
    userId: discordSnowflakeSchema,
    displayName: boundedNfcTextSchema(100, 512),
    bot: z.boolean(),
})
    .strict();
export const synchronizedGuildMemberRoleSchema = z
    .object({
    userId: discordSnowflakeSchema,
    roleId: discordSnowflakeSchema,
})
    .strict();
export const guildSelectorSynchronizationBatchSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    ownerUserId: discordSnowflakeSchema,
    roles: z.array(synchronizedGuildRoleSchema).max(MAX_SYNCHRONIZED_ROLES),
    members: z
        .array(synchronizedGuildMemberSchema)
        .max(MAX_SYNCHRONIZED_MEMBERS_PER_BATCH),
    memberRoles: z
        .array(synchronizedGuildMemberRoleSchema)
        .max(MAX_SYNCHRONIZED_MEMBER_ROLE_EDGES_PER_BATCH),
    batchCursor: discordSnowflakeSchema.nullable(),
    nextBatchCursor: discordSnowflakeSchema.nullable(),
    finalBatch: z.boolean(),
    synchronizedAt: validDateSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.roles.map(({ roleId }) => roleId)).size !==
        value.roles.length) {
        context.addIssue({
            code: "custom",
            message: "Role IDs must be unique",
            path: ["roles"],
        });
    }
    if (new Set(value.members.map(({ userId }) => userId)).size !==
        value.members.length) {
        context.addIssue({
            code: "custom",
            message: "Member IDs must be unique",
            path: ["members"],
        });
    }
    const memberIds = new Set(value.members.map(({ userId }) => userId));
    const memberRoleKeys = new Set();
    value.memberRoles.forEach((memberRole, edgeIndex) => {
        const edgeKey = `${memberRole.userId}:${memberRole.roleId}`;
        if (memberRoleKeys.has(edgeKey)) {
            context.addIssue({
                code: "custom",
                message: "Member-role edges must be unique",
                path: ["memberRoles", edgeIndex],
            });
        }
        memberRoleKeys.add(edgeKey);
        if (!memberIds.has(memberRole.userId)) {
            context.addIssue({
                code: "custom",
                message: "Member-role edges must reference a member in the batch",
                path: ["memberRoles", edgeIndex, "userId"],
            });
        }
    });
    if (value.finalBatch !== (value.nextBatchCursor === null)) {
        context.addIssue({
            code: "custom",
            message: "Only the final batch omits the next batch cursor",
            path: ["nextBatchCursor"],
        });
    }
    if (value.batchCursor !== null &&
        value.batchCursor === value.nextBatchCursor) {
        context.addIssue({
            code: "custom",
            message: "A synchronization batch must advance its cursor",
            path: ["nextBatchCursor"],
        });
    }
});
export const guildSelectorQuerySchema = z
    .object({
    search: selectorSearchSchema.optional(),
    cursor: opaqueCursorSchema.optional(),
    selectedOnly: z.boolean().optional(),
    limit: z
        .int()
        .min(1)
        .max(MAX_SELECTOR_PAGE_SIZE)
        .default(MAX_SELECTOR_PAGE_SIZE),
})
    .strict();
export const guildRoleSelectorItemSchema = z
    .object({
    id: discordSnowflakeSchema,
    name: boundedNfcTextSchema(100, 512),
    position: z.int().nonnegative(),
    managed: z.boolean(),
    active: z.boolean(),
    selected: z.boolean(),
})
    .strict();
export const guildMemberSelectorItemSchema = z
    .object({
    id: discordSnowflakeSchema,
    displayName: boundedNfcTextSchema(100, 512),
    bot: z.literal(false),
    active: z.boolean(),
    selected: z.boolean(),
})
    .strict();
function selectorPageSchema(itemSchema) {
    return z
        .object({
        guildId: discordSnowflakeSchema,
        items: z.array(itemSchema).max(MAX_SELECTOR_PAGE_SIZE),
        nextCursor: opaqueCursorSchema.nullable(),
        snapshotAt: utcTimestampSchema,
        snapshotExpiresAt: utcTimestampSchema,
    })
        .strict()
        .superRefine((value, context) => {
        if (Date.parse(value.snapshotExpiresAt) - Date.parse(value.snapshotAt) !==
            SELECTOR_SNAPSHOT_FRESHNESS_MILLISECONDS) {
            context.addIssue({
                code: "custom",
                message: "Selector snapshot freshness must be exactly two hours",
                path: ["snapshotExpiresAt"],
            });
        }
        const ids = value.items.map((item) => item.id);
        if (new Set(ids).size !== ids.length) {
            context.addIssue({
                code: "custom",
                message: "Selector item IDs must be unique",
                path: ["items"],
            });
        }
    });
}
export const guildRoleSelectorPageSchema = selectorPageSchema(guildRoleSelectorItemSchema);
export const guildMemberSelectorPageSchema = selectorPageSchema(guildMemberSelectorItemSchema);
//# sourceMappingURL=guild-selectors.js.map